import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Divider from 'components/Luxkit/Divider'
import config from 'constants/config'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import { useMutation } from '@tanstack/react-query'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { formToObject } from 'lib/forms/formToObject'
import ReCAPTCHA from 'react-google-recaptcha'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import { useAppDispatch } from 'hooks/reduxHooks'
import { login } from 'api/auth'
import { API_CALL, API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_LOGIN } from 'actions/apiActionConstants'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
  BusinessTravellerBusinessOnboardingStateContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import LineArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'
import IconButton from 'components/Luxkit/Button/IconButton'
import { pushWithRegion } from 'actions/NavigationActions'
import { BUSINESS_TRAVELLER_ROOT_ROUTE } from 'businessTraveller/routes/businessTravellerRoutes'
import { BUSINESS_TRAVELLER_API_ACTIONS } from 'reducers/businessTravellerApiActionReducers'
import requestGetAccountBusiness from 'businessTraveller/api/requestGetAccountBusiness'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const LoginForm = styled.form`
  position: relative;
`

interface MappedStateProps {
  isLoggedIn?: boolean;
}

function BusinessTravellerSignupBusinessExists(props: MappedStateProps) {
  const { isLoggedIn } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const { adminEmail } = useContext(BusinessTravellerBusinessOnboardingStateContext)
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  const isSocialMediaLoginEnabled = !!(config.GOOGLE_APP_ID)

  const loginMutation = useMutation({
    mutationFn: async(userDetails: Pick<BusinessTraveller.UserRegistrationData, 'email' | 'password'>) => {
      const reCaptchaResponse = await handleReCAPTCHA(reCaptchaRef.current)
      return login({ email: userDetails.email }, userDetails.password, reCaptchaResponse)
    },
    onSuccess: (data) => {
      dispatch({
        type: API_CALL_SUCCESS,
        api: USER_LOGIN,
        data,
        source: 'email',
      })

      dispatch({
        type: API_CALL,
        api: BUSINESS_TRAVELLER_API_ACTIONS.FETCH_ACCOUNT_BUSINESS,
        request: () => requestGetAccountBusiness({}),
      })
    },
    onError: (error: App.ApiCallFailure) => {
      passwordInputRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    passwordInputRef.current?.reportValidity()
  }, [])

  const onBackButtonClick = useCallback(() => {
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.STEP_INTO,
      step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.VALIDATE_EMAIL,
    })
  }, [accountSetupDispatch])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsFormSubmitting(true)
    const formData = formToObject<BusinessTraveller.UserRegistrationData>(e.currentTarget)
    loginMutation.mutate({
      ...formData,
      email: adminEmail,
    })
  }, [loginMutation, adminEmail])

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(pushWithRegion(BUSINESS_TRAVELLER_ROOT_ROUTE))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  return (
    <SignupContainer>
      <LoginForm onSubmit={handleSubmission} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" verticalAlign="center">
            <IconButton
              kind="tertiary"
              variant="dark"
              onClick={onBackButtonClick}
              outdent="left"
            >
              <LineArrowLeftIcon />
            </IconButton>
            <Heading variant="heading3">Log in</Heading>
          </Group>
          <BodyText variant="medium">Your email address is already associated with a business account. Please enter your password to proceed</BodyText>
          <PasswordInput
            autoComplete="new-password"
            label="Password"
            minLength={8}
            name="password"
            onChange={passwordOnChange}
            ref={passwordInputRef}
            required
            requiredErrorMessage="Please enter a password"
          />
          <TextButton
            fit="mobile-full-width"
            kind="primary"
            shape="square"
            size="medium"
            type="submit"
            disabled={isFormSubmitting}
          >
            Continue
          </TextButton>
          {isSocialMediaLoginEnabled && <>
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <Divider kind="primary" />
              <BodyText variant="large">or</BodyText>
              <Divider kind="primary" />
            </Group>
            <SocialButtons mode="login" renderId="accountAccessModal"/>
          </>}
        </Group>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reCaptchaRef} />}
      </LoginForm>
    </SignupContainer>
  )
}

export default connect<MappedStateProps, undefined, {}, App.State>((state) => {
  return { isLoggedIn: selectLoggedIn(state) }
})(BusinessTravellerSignupBusinessExists)
